import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import { ArrowLeft, Package, CreditCard, MapPin, User, Phone, Mail, Calendar, RefreshCw, Link2, Search, Download, Palette, Unlink, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ORDER_STATUSES = [
  { value: 'ordered', label: 'Ordered', color: 'bg-blue-100 text-blue-800' },
  { value: 'processing', label: 'Processing', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'dispatched', label: 'Dispatched', color: 'bg-purple-100 text-purple-800' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
  { value: 'refunded', label: 'Refunded', color: 'bg-gray-100 text-gray-800' },
];

// Define allowed status transitions
const ALLOWED_TRANSITIONS = {
  'ordered': ['processing', 'cancelled'],
  'processing': ['dispatched', 'cancelled'],
  'dispatched': ['delivered', 'cancelled'],
  'delivered': [], // Final state
  'cancelled': ['refunded'],
  'refunded': [], // Final state
};

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [availableLinks, setAvailableLinks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [associatingLink, setAssociatingLink] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchOrderDetails();
  }, [orderId]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/orders/${orderId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      setOrder(response.data);
      
      // Fetch available links if no link is associated
      if (!response.data.associated_dynamic_link_id) {
        fetchAvailableLinks();
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchAvailableLinks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      console.log('Fetching available links...');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/dynamic-links/available`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Available links response:', response);
      if (response.ok) {
        console.log('Links data:', response.data);
        setAvailableLinks(response.data.links || []);
        console.log('Available links set:', response.data.links?.length || 0);
      } else {
        console.error('Failed to fetch available links:', response);
      }
    } catch (err) {
      console.error('Error fetching available links:', err);
    }
  };

  const handleAssociateDynamicLink = async (linkId) => {
    setAssociatingLink(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/orders/${orderId}/dynamic-link`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dynamic_link_id: linkId }),
      });

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to associate dynamic link');
      }

      // Update local state
      setOrder(response.data.order);
      setAvailableLinks([]); // Clear available links
      setSearchTerm('');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAssociatingLink(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const currentStatus = order?.order_status || 'ordered';
    if (newStatus === currentStatus) return;

    // Confirm before cancelling — irreversible to most flows + auto-unlinks dynamic link.
    if (newStatus === 'cancelled') {
      const hasLink = !!order?.associated_dynamic_link_id;
      const msg = hasLink
        ? 'Cancel this order?\n\nThis will also UNLINK the dynamic link currently attached to it.'
        : 'Are you sure you want to cancel this order?\n\nThis action moves the order to "Cancelled".';
      if (!window.confirm(msg)) return;
    }

    // If marking as delivered and the order is still unpaid, offer to also mark paid.
    let alsoMarkPaid = false;
    if (newStatus === 'delivered' && order?.payment_status !== 'paid') {
      alsoMarkPaid = window.confirm(
        'Mark this order as Delivered.\n\nDo you also want to mark it as PAID?\n\nClick OK to mark as paid, or Cancel to leave the payment status as is.'
      );
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      // Update local state
      setOrder(prev => ({
        ...prev,
        order_status: newStatus,
        // If cancelled, the backend also clears the dynamic-link association.
        ...(newStatus === 'cancelled'
          ? { associated_dynamic_link_id: null, dynamic_link_details: null }
          : {}),
      }));

      // Optionally mark as paid right after a successful delivered transition
      if (alsoMarkPaid) {
        await handleMarkPaid({ silent: true });
      }

      setUpdating(false);
    } catch (err) {
      setError(err.message);
      setUpdating(false);
    }
  };

  const handleMarkPaid = async ({ silent = false } = {}) => {
    if (order?.payment_status === 'paid') return;
    if (!silent && !window.confirm('Mark this order as PAID?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/orders/${orderId}/mark-paid`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to mark order as paid');
      }
      setOrder(prev => ({
        ...prev,
        payment_status: 'paid',
        paid_at: new Date().toISOString(),
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnlinkDynamicLink = async () => {
    if (!order?.associated_dynamic_link_id) return;
    if (!window.confirm('Unlink the dynamic link from this order?\n\nThe link will become available to associate with another order.')) {
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/orders/${orderId}/dynamic-link`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to unlink dynamic link');
      }
      setOrder(prev => ({
        ...prev,
        associated_dynamic_link_id: null,
        dynamic_link_details: null,
      }));
      // Refetch available links so admin can re-associate
      fetchAvailableLinks();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    const statusObj = ORDER_STATUSES.find(s => s.value === status?.toLowerCase());
    return statusObj ? statusObj.color : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading order details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !order) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error || 'Order not found'}
          </div>
          <Button
            onClick={() => navigate('/admin/orders')}
            className="mt-4 bg-navy text-white hover:bg-navy/90"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/orders')}
              className="bg-gray-100 text-navy hover:bg-gray-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-extrabold text-navy">Order #{order.transaction_id?.slice(0, 12)}</h1>
              <p className="text-sm text-gray-500 mt-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {formatDate(order.created_at)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Information */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Order Information</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Product:</span>
                  <span className="font-semibold text-navy">{order.order_data?.cardType || 'N/A'}</span>
                </div>
                {order.order_data?.selectedMaterial && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Material:</span>
                    <span className="font-semibold text-navy">{order.order_data.selectedMaterial.name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-semibold text-navy">${order.order_data?.selectedMaterial?.price || order.amount}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Quantity:</span>
                  <span className="font-semibold text-navy">{order.order_data?.quantity || 1}</span>
                </div>
                {order.coupon && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold text-navy" data-testid="admin-order-subtotal">
                        ${(order.subtotal ?? order.amount).toFixed ? (order.subtotal ?? order.amount).toFixed(2) : (order.subtotal ?? order.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">
                        Coupon: <span className="font-mono text-navy">{order.coupon.code}</span>
                        {order.coupon.discount_type === 'percentage'
                          ? ` (${order.coupon.discount_value}% off)`
                          : ` ($${order.coupon.discount_value} off)`}
                      </span>
                      <span className="font-semibold text-green-700" data-testid="admin-order-discount">
                        -${Number(order.coupon.discount_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Payment Type:</span>
                  <span className="font-semibold text-navy">
                    {order.payment_type === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Payment Status:</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                      order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                    </span>
                    {order.payment_status !== 'paid' && (
                      <button
                        onClick={() => handleMarkPaid()}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition"
                        title="Mark this order as paid"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Mark as Paid
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 text-lg">Total Amount:</span>
                  <span className="font-bold text-navy text-xl">${order.amount}</span>
                </div>
              </div>
            </div>

            {/* Order Status Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <RefreshCw className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Order Status</h2>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Status
                </label>
                <select
                  value={order.order_status || 'ordered'}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updating}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ORDER_STATUSES.map((status) => {
                    const currentStatus = order.order_status || 'ordered';
                    const allowedNextStatuses = ALLOWED_TRANSITIONS[currentStatus] || [];
                    
                    // An option is enabled if:
                    // 1. It's the current status (to show current state)
                    // 2. It's an allowed next status based on transitions
                    // 3. For dispatched/delivered: also requires dynamic link
                    const isCurrentStatus = status.value === currentStatus;
                    const isAllowedTransition = allowedNextStatuses.includes(status.value);
                    const requiresDynamicLink = (status.value === 'dispatched' || status.value === 'delivered');
                    const hasDynamicLink = !!order.associated_dynamic_link_id;
                    
                    // Disable if not current and not allowed, OR if requires link but doesn't have one
                    const isDisabled = (!isCurrentStatus && !isAllowedTransition) || 
                                      (requiresDynamicLink && !hasDynamicLink && !isCurrentStatus);
                    
                    let disabledReason = '';
                    if (isDisabled && requiresDynamicLink && !hasDynamicLink) {
                      disabledReason = ' (Requires Dynamic Link)';
                    } else if (isDisabled && !isCurrentStatus && !isAllowedTransition) {
                      disabledReason = ' (Not allowed from current status)';
                    }
                    
                    return (
                      <option key={status.value} value={status.value} disabled={isDisabled}>
                        {status.label}{disabledReason}
                      </option>
                    );
                  })}
                </select>
                {updating && (
                  <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating status...
                  </p>
                )}
                {!order.associated_dynamic_link_id && (
                  <p className="text-sm text-amber-600 mt-2 flex items-center gap-2">
                    ⚠️ Dynamic link must be assigned before marking as dispatched or delivered
                  </p>
                )}
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-blue-900 mb-1">Status Flow:</p>
                  <p className="text-xs text-blue-700">
                    Ordered → Processing → Dispatched → Delivered
                  </p>
                  <p className="text-xs text-blue-700">
                    Or: Ordered → Cancelled → Refunded
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Status Timeline</p>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.order_status || 'ordered')}`}>
                    {ORDER_STATUSES.find(s => s.value === (order.order_status || 'ordered'))?.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Payment Information</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Payment Type:</span>
                  <span className="font-semibold text-navy">
                    {order.payment_type === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="font-semibold text-navy">{order.payment_method || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-bold text-navy text-lg">${order.amount}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="font-mono text-sm text-gray-900">{order.transaction_id || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Associated Dynamic Link */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-navy" />
                  <h2 className="text-xl font-bold text-navy">Associated Dynamic Link</h2>
                </div>
                {order.associated_dynamic_link_id && (
                  <button
                    onClick={handleUnlinkDynamicLink}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 transition"
                    title="Unlink the dynamic link from this order"
                  >
                    <Unlink className="w-3.5 h-3.5" />
                    Unlink
                  </button>
                )}
              </div>
              
              {order.associated_dynamic_link_id && order.dynamic_link_details ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Card Link:</p>
                  <a 
                    href={`${window.location.origin}/${order.dynamic_link_details.code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-navy font-mono text-sm hover:underline break-all"
                  >
                    {window.location.origin}/{order.dynamic_link_details.code}
                  </a>
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-xs text-gray-500">QR Code URL:</p>
                    <p className="text-xs font-mono text-gray-700 mt-1">
                      {window.location.origin}/{order.dynamic_link_details.code}?src_id=qr
                    </p>
                    <p className="text-xs text-gray-500 mt-2">NFC Tag URL:</p>
                    <p className="text-xs font-mono text-gray-700 mt-1">
                      {window.location.origin}/{order.dynamic_link_details.code}?src_id=nfc
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-xs text-gray-500">Redirect URL:</p>
                    <a 
                      href={order.dynamic_link_details.redirect_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 break-all block"
                    >
                      {order.dynamic_link_details.redirect_url}
                    </a>
                  </div>
                  <div className="mt-3 pt-3 border-t border-green-200 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">QR Scans:</p>
                      <p className="text-lg font-bold text-blue-600 mt-1">{order.dynamic_link_details.qr_scans || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">NFC Taps:</p>
                      <p className="text-lg font-bold text-purple-600 mt-1">{order.dynamic_link_details.nfc_taps || 0}</p>
                    </div>
                  </div>
                </div>
              ) : order.associated_dynamic_link_id ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Link associated but details not found</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">Select a dynamic link to associate with this order:</p>
                  
                  {/* Search Input */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by code or redirect URL..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                    />
                  </div>

                  {/* Links Dropdown */}
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    {availableLinks
                      .filter(link => 
                        link.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        link.redirect_url.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((link) => (
                        <button
                          key={link.id}
                          onClick={() => handleAssociateDynamicLink(link.id)}
                          disabled={associatingLink}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <p className="font-semibold text-navy text-sm">{link.code}</p>
                          <p className="text-xs text-gray-500 truncate">{link.redirect_url}</p>
                          <p className="text-xs text-gray-400 mt-1">Type: {link.link_type || 'review_card'}</p>
                        </button>
                      ))}
                    {availableLinks.filter(link => 
                      link.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      link.redirect_url.toLowerCase().includes(searchTerm.toLowerCase())
                    ).length === 0 && (
                      <p className="text-center py-6 text-gray-500 text-sm">
                        {searchTerm ? 'No links found matching your search' : 'No available links'}
                      </p>
                    )}
                  </div>
                  
                  {associatingLink && (
                    <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Associating link...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Card Images - Coming Soon */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-navy mb-4">Card Images</h2>
              <p className="text-gray-500 text-center py-8">Card images will be displayed here once available</p>
            </div>
          </div>

          {/* Right Column - Customer & Shipping Info */}
          <div className="space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Customer Information</h2>
              </div>
              <div className="space-y-3">
                {order.order_data?.displayName && (
                  <div className="flex items-start gap-2 py-2 border-b border-gray-100">
                    <User className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Business Name</p>
                      <p className="font-semibold text-navy">{order.order_data.displayName}</p>
                    </div>
                  </div>
                )}
                {order.shipping_address?.email && (
                  <div className="flex items-start gap-2 py-2 border-b border-gray-100">
                    <Mail className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-semibold text-navy">{order.shipping_address.email}</p>
                    </div>
                  </div>
                )}
                {order.shipping_address?.phone && (
                  <div className="flex items-start gap-2 py-2">
                    <Phone className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-semibold text-navy">{order.shipping_address.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Customisations Section */}
            {(order.order_data?.displayName || order.order_data?.logoPreview) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Palette className="w-5 h-5 text-navy" />
                  <h2 className="text-xl font-bold text-navy">Customisations</h2>
                </div>
                <div className="space-y-4">
                  {order.order_data?.displayName && (
                    <div className="py-2 border-b border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Business Name</p>
                      <p className="font-semibold text-navy text-lg">{order.order_data.displayName}</p>
                    </div>
                  )}
                  {order.order_data?.logoPreview && (
                    <div className="py-2">
                      <p className="text-xs text-gray-500 mb-2">Uploaded Logo</p>
                      <div className="flex items-start gap-4">
                        <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                          <img 
                            src={order.order_data.logoPreview} 
                            alt="Customer Logo" 
                            className="w-24 h-24 object-contain"
                          />
                        </div>
                        <Button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = order.order_data.logoPreview;
                            link.download = `logo_${order.transaction_id}.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="bg-navy text-white hover:bg-navy/90 rounded-lg px-4 py-2 text-sm"
                          data-testid="download-logo-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Logo
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Shipping Address */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Shipping Address</h2>
              </div>
              {order.shipping_address ? (
                <div className="text-gray-700 space-y-1">
                  <p className="font-semibold text-navy">
                    {order.shipping_address.firstName} {order.shipping_address.lastName}
                  </p>
                  <p>{order.shipping_address.addressLine1}</p>
                  {order.shipping_address.addressLine2 && (
                    <p>{order.shipping_address.addressLine2}</p>
                  )}
                  <p>
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}
                  </p>
                  <p>{order.shipping_address.country}</p>
                </div>
              ) : (
                <p className="text-gray-500">No shipping address available</p>
              )}
            </div>

            {/* Billing Address */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Billing Address</h2>
              </div>
              {order.billing_address ? (
                (() => {
                  // Check if billing address is same as shipping
                  const isSameAsShipping = order.shipping_address && order.billing_address &&
                    order.shipping_address.firstName === order.billing_address.firstName &&
                    order.shipping_address.lastName === order.billing_address.lastName &&
                    order.shipping_address.addressLine1 === order.billing_address.addressLine1 &&
                    order.shipping_address.city === order.billing_address.city &&
                    order.shipping_address.state === order.billing_address.state &&
                    order.shipping_address.zipCode === order.billing_address.zipCode;

                  return isSameAsShipping ? (
                    <p className="text-gray-600 italic">Same as shipping address</p>
                  ) : (
                    <div className="text-gray-700 space-y-1">
                      <p className="font-semibold text-navy">
                        {order.billing_address.firstName} {order.billing_address.lastName}
                      </p>
                      <p>{order.billing_address.addressLine1}</p>
                      {order.billing_address.addressLine2 && (
                        <p>{order.billing_address.addressLine2}</p>
                      )}
                      <p>
                        {order.billing_address.city}, {order.billing_address.state} {order.billing_address.zipCode}
                      </p>
                      <p>{order.billing_address.country}</p>
                    </div>
                  );
                })()
              ) : (
                <p className="text-gray-500">No billing address available</p>
              )}
            </div>

            {/* Card Details */}
            {order.order_data?.address && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-navy mb-4">Card Details</h2>
                <div className="text-gray-700 space-y-2">
                  <p className="text-sm text-gray-500">Address on Card:</p>
                  <p className="font-semibold">{order.order_data.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
